/**
 * Legal identity settings (NOS-167): legal name + EIN used on donation
 * receipts. Fiscal children see a note that receipts carry their sponsor's
 * identity instead.
 */
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { supabase } from "@/lib/supabase"
import { normalizeEin, validateEinInput } from "@/lib/ein"

export function LegalIdentityCard({ orgId }: { orgId: string }) {
  const [legalName, setLegalName] = useState("")
  const [ein, setEin] = useState("")
  const [mailingAddress, setMailingAddress] = useState("")
  const [einError, setEinError] = useState<string | null>(null)
  const [fiscallySponsored, setFiscallySponsored] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      const [{ data: org }, { data: link }] = await Promise.all([
        supabase.from("organizations").select("legal_name, ein, mailing_address").eq("id", orgId).maybeSingle(),
        supabase
          .from("sponsorships")
          .select("mode, status")
          .eq("child_org_id", orgId)
          .maybeSingle(),
      ])
      if (cancelled) return
      setLegalName(org?.legal_name ?? "")
      setEin(org?.ein ?? "")
      setMailingAddress(org?.mailing_address ?? "")
      setFiscallySponsored(
        link?.mode === "fiscal" && (link?.status === "active" || link?.status === "transferring")
      )
      setLoading(false)
    }
    load()
    return () => {
      cancelled = true
    }
  }, [orgId])

  const handleSave = async () => {
    const check = validateEinInput(ein)
    if (!check.valid) {
      setEinError(check.error ?? "Invalid EIN")
      return
    }
    setEinError(null)
    setSaving(true)
    const { error } = await supabase
      .from("organizations")
      .update({
        legal_name: legalName.trim() || null,
        ein: normalizeEin(ein),
        mailing_address: mailingAddress.trim() || null,
        updated_at: new Date().toISOString(),
      })
      .eq("id", orgId)
    setSaving(false)
    if (error) {
      toast.error(error.message)
      return
    }
    setEin(normalizeEin(ein) ?? "")
    toast.success("Legal identity saved")
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Legal identity</CardTitle>
        <CardDescription>
          Shown on donation receipts for tax purposes. Leave legal name blank to use your display
          name.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {fiscallySponsored && (
          <p className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
            Your donations are received under your fiscal sponsor — receipts carry the sponsor's
            legal name and EIN with a "project of" disclosure.
          </p>
        )}
        <div className="space-y-2">
          <Label htmlFor="legal-name">Legal name</Label>
          <Input
            id="legal-name"
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
            placeholder="Riverside Youth Collective Inc"
            disabled={loading || saving}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="legal-ein">EIN</Label>
          <Input
            id="legal-ein"
            value={ein}
            onChange={(e) => {
              setEin(e.target.value)
              setEinError(null)
            }}
            placeholder="12-3456789"
            disabled={loading || saving}
          />
          {einError && <p className="text-sm text-destructive">{einError}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="legal-address">Mailing address</Label>
          <Input
            id="legal-address"
            value={mailingAddress}
            onChange={(e) => setMailingAddress(e.target.value)}
            placeholder="123 Main St, Suite 4, Anytown, CA 92800"
            disabled={loading || saving}
          />
          <p className="text-xs text-muted-foreground">
            Used in grant reporting exports (e.g. Schedule I).
          </p>
        </div>
        <Button size="sm" onClick={handleSave} disabled={loading || saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </CardContent>
    </Card>
  )
}
